import { Row, Col, Card, Statistic, Table, Tag, Typography, Spin, Alert, Tooltip, Button } from 'antd';
import {
  CalendarOutlined, CheckCircleOutlined, MedicineBoxOutlined,
  SyncOutlined, ClockCircleOutlined, RightOutlined,
  BankOutlined, UserOutlined, HeartOutlined, SolutionOutlined, StopOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { dashboardApi } from '../../../api/dashboard.api';
import { usePermission } from '../../../hooks/usePermission';
import { formatDate } from '../../../utils/helpers';
import { STATUS_COLORS } from '../../../utils/constants';

const { Title } = Typography;

const StatCard = ({ title, value, icon, color = 'var(--wl-primary)', navigateTo }) => {
  const navigate = useNavigate();
  const clickable = !!navigateTo;
  return (
    <Tooltip title={clickable ? `View ${title}` : undefined}>
      <Card
        className="card-stats"
        style={{ borderLeft: `4px solid ${color}`, cursor: clickable ? 'pointer' : 'default', transition: 'box-shadow 0.2s' }}
        onClick={clickable ? () => navigate(navigateTo) : undefined}
        hoverable={clickable}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Statistic
            title={
              <span style={{ fontSize: 13, color: 'var(--wl-text)', fontWeight: 600 }}>
                {title}
                {clickable && <RightOutlined style={{ fontSize: 10, marginLeft: 4, color: 'var(--wl-text)', opacity: 0.7 }} />}
              </span>
            }
            value={value ?? 0}
            valueStyle={{ color, fontSize: 28, fontWeight: 700, fontFamily: "'Roboto', sans-serif" }}
          />
          <div style={{ fontSize: 36, color, opacity: 0.2 }}>{icon}</div>
        </div>
      </Card>
    </Tooltip>
  );
};

const upcomingEventColumns = [
  {
    title: 'Event', dataIndex: 'title', key: 'title',
    ellipsis: { showTitle: false },
    render: (v) => <Tooltip title={v}>{v}</Tooltip>,
  },
  {
    title: 'School', key: 'school',
    ellipsis: { showTitle: false },
    render: (_, r) => {
      const name = r.school?.name || '-';
      return <Tooltip title={name}>{name}</Tooltip>;
    },
  },
  { title: 'Date', dataIndex: 'eventDate', key: 'eventDate', render: (v) => formatDate(v) },
  {
    title: 'Status', dataIndex: 'status', key: 'status',
    render: (v) => <Tag color={STATUS_COLORS[v]}>{v}</Tag>,
  },
];

const Dashboard = () => {
  const { isAdmin, isDoctor, isSchoolStaff, isReferralDoctor, role } = usePermission();
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard', role],
    queryFn: () => dashboardApi.getData(),
    enabled: !!role,
  });

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return <Alert message="Failed to load dashboard data" type="error" showIcon />;
  }

  const { stats = {}, upcomingEvents = [], assignedEvents = [] } = data?.data || {};

  const doctorUpcoming  = assignedEvents.filter((e) => e.status === 'upcoming').length;
  const doctorOngoing   = assignedEvents.filter((e) => e.status === 'ongoing').length;
  const doctorCompleted = assignedEvents.filter((e) => e.status === 'completed').length;

  const eventList = isDoctor ? assignedEvents : upcomingEvents;

  return (
    <div>
      <Title level={4} style={{ marginBottom: 24 }}>Dashboard Overview</Title>

      {/* ── Admin top row: totals ───────────────────────────────── */}
      {isAdmin && (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} lg={6}>
            <StatCard title="Total Schools"  value={stats.totalSchools}  icon={<BankOutlined />}   color="var(--wl-primary)" navigateTo="/schools" />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard title="Total Doctors"  value={stats.totalDoctors}  icon={<UserOutlined />}   color="var(--wl-primary)" navigateTo="/doctors" />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard title="Total Events"   value={stats.totalEvents}   icon={<CalendarOutlined />} color="var(--wl-primary)" navigateTo="/events" />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard title="Total Children" value={stats.totalChildren} icon={<HeartOutlined />}  color="var(--wl-primary)" navigateTo="/children" />
          </Col>
        </Row>
      )}

      {/* ── Admin: event status row ────────────────────────────── */}
      {isAdmin && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <StatCard title="Upcoming Events"    value={stats.upcomingCount}     icon={<ClockCircleOutlined />}  color="var(--wl-primary)" navigateTo="/events?tab=upcoming" />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard title="In Progress Events" value={stats.ongoingCount}      icon={<SyncOutlined />}         color="var(--wl-primary)" navigateTo="/events?tab=ongoing" />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard title="Completed Events"   value={stats.completedCount}    icon={<CheckCircleOutlined />}  color="var(--wl-primary)" navigateTo="/events?tab=completed" />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard title="Treatment Required" value={stats.treatmentRequired} icon={<MedicineBoxOutlined />}  color="var(--wl-primary)" />
          </Col>
        </Row>
      )}

      {/* ── Referral Doctor: referred / approved / rejected counts ── */}
      {isReferralDoctor && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={8}>
            <StatCard title="No. of Children Referred" value={stats.totalReferred} icon={<SolutionOutlined />} color="var(--wl-primary)" navigateTo="/referrals" />
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <StatCard title="Approved" value={stats.totalApproved} icon={<CheckCircleOutlined />} color="#52c41a" navigateTo="/referrals" />
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <StatCard title="Rejected" value={stats.totalRejected} icon={<StopOutlined />} color="#ff4d4f" navigateTo="/referrals" />
          </Col>
        </Row>
      )}

      {/* ── Doctor: counts from assigned events only ───────────── */}
      {isDoctor && !isReferralDoctor && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <StatCard title="My Assigned Events" value={assignedEvents.length}   icon={<CalendarOutlined />}     color="var(--wl-primary)" navigateTo="/events" />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard title="Upcoming"           value={doctorUpcoming}          icon={<ClockCircleOutlined />}  color="var(--wl-primary)" navigateTo="/events?tab=upcoming" />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard title="Children Checked"   value={stats.totalChecked}      icon={<CheckCircleOutlined />}  color="var(--wl-primary)" />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard title="Treatment Required" value={stats.treatmentRequired} icon={<MedicineBoxOutlined />}  color="var(--wl-primary)" />
          </Col>
        </Row>
      )}

      {/* ── School Staff: assigned events stats ────────────────── */}
      {!isAdmin && !isDoctor && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <StatCard title="Assigned Events"     value={stats.totalEvents}      icon={<CalendarOutlined />}     color="var(--wl-primary)" navigateTo="/events" />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard title="Upcoming Events"     value={stats.upcomingCount}    icon={<ClockCircleOutlined />}  color="var(--wl-primary)" navigateTo="/events?tab=upcoming" />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard title="In Progress Events"  value={stats.ongoingCount}     icon={<SyncOutlined />}         color="var(--wl-primary)" navigateTo="/events?tab=ongoing" />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard title="Completed Events"    value={stats.completedCount}   icon={<CheckCircleOutlined />}  color="var(--wl-primary)" navigateTo="/events?tab=completed" />
          </Col>
        </Row>
      )}

      {/* ── Events table ───────────────────────────────────────── */}
      {!isReferralDoctor && (
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Card title={isDoctor ? 'My Assigned Events' : 'Upcoming Events'}>
            <Table
              dataSource={eventList.slice(0, 5)}
              columns={upcomingEventColumns}
              rowKey="id"
              pagination={false}
              size="small"
              locale={{ emptyText: isDoctor ? 'No assigned events' : 'No upcoming events' }}
            />
            {eventList.length > 5 && (
              <div style={{ textAlign: 'center', marginTop: 12 }}>
                <Button
                  type="link"
                  onClick={() => navigate('/events?tab=upcoming')}
                  style={{ color: 'var(--wl-primary)', fontWeight: 600 }}
                >
                  View All
                </Button>
              </div>
            )}
          </Card>
        </Col>
      </Row>
      )}
    </div>
  );
};

export default Dashboard;
